import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { ViewContext } from 'components/lib';
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  WMSTileLayer,
  Marker,
  GeoJSON,
  Circle,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import { Target03Icon } from 'components/icons';
import { lv95ToWgs84ForOneCoordinate } from './coordinateTransforms';
import RenderIfThereIsAPolygon from './RenderIfThereIsAPolygon';
import MapTracker from './MapTracker.js';
import MousePositionPopup from 'components/map/MousePositionPopup.js';
import Copyright from 'components/map/Copyright.js';
import ShowHideButtonsForMaps from './ShowHideButtonsForMaps';
import type { GridParams } from 'contexts/GridContext';
import type { CoordinateSystemPreference } from 'utils/coordinateSystemManager';
import { PROJECT_LOCATION_RADIUS_METERS } from 'views/add-project/MandatoryFields';
import { synchronizeMapView, type GridMapType, type MapViewsState } from './mapConfig';
import { getSelectedSideAngle } from 'lib/leafletHelper';
import { CADASTRAL_EDGE_CLICK_MAX_DISTANCE_PX, CADASTRAL_MIN_ZOOM } from 'constants/grid';
import { getLayerMap, getLayerOuterRing } from '../cadastral/cadastralShared';
import {
  CADASTRAL_IDENTIFY_URL,
  CADASTRAL_LAYER_NAME,
  extractFirstFeature,
  getImageDisplayString,
  getMapExtentString,
  getSwissPointStringFromLatLng,
  type GeoAdminIdentifyFeature,
} from 'utils/geoAdminIdentifyHelper';

type Lv95Coordinate = { x: number; y: number };
type GridStyle = typeof import('../styles/grid.tailwind').default;

interface AnalysisAreaCenterMarkerSyncProps {
  analysisArea: Lv95Coordinate[];
  analysisAreas?: Lv95Coordinate[][];
  manualCenter: Lv95Coordinate | null;
  mapType: GridMapType;
}

const getCombinedAnalysisAreasCenterLatLng = (
  analysisArea: Lv95Coordinate[],
  analysisAreas: Lv95Coordinate[][] | undefined,
  manualCenter: Lv95Coordinate | null
): [number, number] | null => {
  const manualCenterX = manualCenter?.x;
  const manualCenterY = manualCenter?.y;
  const hasManualCenter =
    Number.isFinite(manualCenterX) &&
    Number.isFinite(manualCenterY) &&
    manualCenterX != null &&
    manualCenterY != null &&
    (manualCenterX !== 0 || manualCenterY !== 0);

  if (hasManualCenter) {
    const manual = lv95ToWgs84ForOneCoordinate(Number(manualCenterX), Number(manualCenterY));
    return [manual.lat, manual.lng];
  }

  const polygons =
    Array.isArray(analysisAreas) && analysisAreas.length > 0
      ? analysisAreas
      : Array.isArray(analysisArea) && analysisArea.length > 0
        ? [analysisArea]
        : [];
  const points = polygons.flat().filter((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y));

  if (points.length === 0) {
    return null;
  }

  const bounds = points.reduce(
    (acc, point) => ({
      minX: Math.min(acc.minX, point.x),
      maxX: Math.max(acc.maxX, point.x),
      minY: Math.min(acc.minY, point.y),
      maxY: Math.max(acc.maxY, point.y),
    }),
    {
      minX: points[0].x,
      maxX: points[0].x,
      minY: points[0].y,
      maxY: points[0].y,
    }
  );

  const center = lv95ToWgs84ForOneCoordinate((bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2);
  return [center.lat, center.lng];
};

const createReadOnlyCenterMarkerIcon = () => {
  const markerSize = 16;
  return L.divIcon({
    className: 'custom-center-marker',
    iconSize: [markerSize, markerSize],
    iconAnchor: [markerSize / 2, markerSize / 2],
    html: `<div style="width:${markerSize}px;height:${markerSize}px;line-height:${markerSize}px;text-align:center;font-size:14px;color:#e53935;">✖</div>`,
  });
};

export const ReadOnlyAnalysisAreaCenterMarkerSync = ({
  analysisArea,
  analysisAreas,
  manualCenter,
}: AnalysisAreaCenterMarkerSyncProps) => {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    const center = getCombinedAnalysisAreasCenterLatLng(analysisArea, analysisAreas, manualCenter);
    if (!center) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    if (!markerRef.current) {
      markerRef.current = L.marker(center, {
        icon: createReadOnlyCenterMarkerIcon(),
        draggable: false,
        interactive: false,
      }).addTo(map);
    } else {
      markerRef.current.setLatLng(center);
      if (!map.hasLayer(markerRef.current)) {
        markerRef.current.addTo(map);
      }
    }

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
    };
  }, [analysisArea, analysisAreas, manualCenter, map]);

  return null;
};

interface RenderCadastralLayerProps {
  cadastralOpacity: number;
  crs2056: L.CRS;
  cadastralLayerKey: number;
  onCadastralTileLoadStart?: L.LeafletEventHandlerFn;
  onCadastralTileLoad?: L.LeafletEventHandlerFn;
  onCadastralLayerLoad?: L.LeafletEventHandlerFn;
  onCadastralTileError?: L.LeafletEventHandlerFn;
}

const renderCadastralLayer = ({
  cadastralOpacity,
  crs2056,
  cadastralLayerKey,
  onCadastralTileLoadStart,
  onCadastralTileLoad,
  onCadastralLayerLoad,
  onCadastralTileError,
}: RenderCadastralLayerProps) => {
  const eventHandlers = Object.fromEntries(
    Object.entries({
      tileloadstart: onCadastralTileLoadStart,
      tileload: onCadastralTileLoad,
      load: onCadastralLayerLoad,
      tileerror: onCadastralTileError,
    }).filter(([, handler]) => typeof handler === 'function')
  );

  return (
    <WMSTileLayer
      key={cadastralLayerKey}
      url="https://wms.geo.admin.ch/"
      layers={CADASTRAL_LAYER_NAME}
      format="image/png"
      transparent={true}
      version="1.3.0"
      opacity={cadastralOpacity}
      zIndex={2}
      maxZoom={28}
      crs={crs2056}
      eventHandlers={eventHandlers}
    />
  );
};

const mapControlsStyle = {
  position: 'absolute',
  top: '13px',
  left: '13px',
  zIndex: 1000,
  minWidth: '30px',
  width: '30px',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  borderRadius: '18px',
  background: 'rgba(255, 255, 255, 0.82)',
  color: '#64748b',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
  backdropFilter: 'blur(6px)',
  boxSizing: 'border-box',
  overflow: 'hidden',
  cursor: 'pointer',
} as const;

const mapControlButtonStyle = {
  width: '100%',
  height: '30px',
  padding: '0',
  border: '0',
  background: 'transparent',
  color: '#64748b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
} as const;

const mapControlDividerStyle = {
  width: '100%',
  height: '1px',
  background: 'rgba(15, 23, 42, 0.08)',
} as const;

interface CadastralParcelInteractionProps {
  enabled: boolean;
  isDrawingRef?: MutableRefObject<boolean> | null;
  onHoverFeatureChange: (feature: GeoAdminIdentifyFeature | null) => void;
  onParcelSelected?: (feature: GeoAdminIdentifyFeature) => void;
  onParcelMissClick?: () => void;
}

const CadastralParcelInteraction = ({
  enabled,
  isDrawingRef,
  onHoverFeatureChange,
  onParcelSelected,
  onParcelMissClick,
}: CadastralParcelInteractionProps) => {
  const context = useContext(ViewContext);
  const map = useMap();
  const hoverAbortControllerRef = useRef<AbortController | null>(null);
  const clickAbortControllerRef = useRef<AbortController | null>(null);
  const lastHoverRequestTsRef = useRef(0);
  const hasShownIdentifyErrorRef = useRef(false);
  const identifyErrorResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showIdentifyErrorToast = useCallback(() => {
    if (hasShownIdentifyErrorRef.current) {
      return;
    }

    hasShownIdentifyErrorRef.current = true;
    context.notification.show(
      'Unable to load cadastral parcel data right now. Please try again in a moment.',
      'error',
      true,
      'toast'
    );

    if (identifyErrorResetTimeoutRef.current) {
      clearTimeout(identifyErrorResetTimeoutRef.current);
    }

    identifyErrorResetTimeoutRef.current = setTimeout(() => {
      hasShownIdentifyErrorRef.current = false;
      identifyErrorResetTimeoutRef.current = null;
    }, 4000);
  }, [context.notification]);

  const identifyParcel = useCallback(
    async (latlng: L.LatLng, signal: AbortSignal) => {
      if (!enabled) return null;

      const params = new URLSearchParams({
        geometryType: 'esriGeometryPoint',
        geometry: getSwissPointStringFromLatLng(map, latlng),
        sr: '2056',
        tolerance: '8',
        returnGeometry: 'true',
        geometryFormat: 'geojson',
        layers: `all:${CADASTRAL_LAYER_NAME}`,
        mapExtent: getMapExtentString(map),
        imageDisplay: getImageDisplayString(map),
        lang: 'en',
      });

      const response = await fetch(`${CADASTRAL_IDENTIFY_URL}?${params.toString()}`, {
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        throw new Error(`Cadastral identify failed with status ${response.status}`);
      }

      const json = await response.json();
      return extractFirstFeature(json);
    },
    [enabled, map]
  );

  useMapEvents({
    mousemove: async (event) => {
      if (!enabled || isDrawingRef?.current) return;

      const now = Date.now();
      if (now - lastHoverRequestTsRef.current < 150) return;
      lastHoverRequestTsRef.current = now;

      if (hoverAbortControllerRef.current) {
        hoverAbortControllerRef.current.abort();
      }

      const controller = new AbortController();
      hoverAbortControllerRef.current = controller;

      try {
        const feature = await identifyParcel(event.latlng, controller.signal);
        onHoverFeatureChange(feature);
      } catch (error) {
        if (!(error instanceof Error) || error.name !== 'AbortError') {
          console.error('Failed to identify cadastral parcel on hover:', error);
          showIdentifyErrorToast();
        }
      }
    },

    mouseout: () => {
      if (!enabled || isDrawingRef?.current) return;
      onHoverFeatureChange(null);
    },

    click: async (event) => {
      if (!enabled || isDrawingRef?.current) return;

      if (clickAbortControllerRef.current) {
        clickAbortControllerRef.current.abort();
      }

      const controller = new AbortController();
      clickAbortControllerRef.current = controller;

      try {
        const feature = await identifyParcel(event.latlng, controller.signal);
        if (feature) {
          onParcelSelected?.(feature);
          return;
        }
        onParcelMissClick?.();
      } catch (error) {
        if (!(error instanceof Error) || error.name !== 'AbortError') {
          console.error('Failed to identify cadastral parcel on click:', error);
          showIdentifyErrorToast();
        }
      }
    },
  });

  useEffect(() => {
    return () => {
      hoverAbortControllerRef.current?.abort();
      clickAbortControllerRef.current?.abort();
      if (identifyErrorResetTimeoutRef.current) {
        clearTimeout(identifyErrorResetTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

const useProjectedCoordsToLatLng = () => {
  const map = useMap();

  return useCallback(
    (coords: [number, number]) => {
      const point = L.point(coords[0], coords[1]);
      return map.options.crs!.unproject(point);
    },
    [map]
  );
};

interface CadastralFeatureOverlayProps {
  feature: GeoAdminIdentifyFeature | null;
  style: L.PathOptions;
}

const CadastralFeatureOverlay = ({ feature, style }: CadastralFeatureOverlayProps) => {
  const geoJsonRef = useRef<L.GeoJSON | null>(null);
  const coordsToLatLng = useProjectedCoordsToLatLng();

  useEffect(() => {
    if (!geoJsonRef.current || !feature) return;

    try {
      const layer = geoJsonRef.current;
      const bounds = layer.getBounds?.();
      if (bounds?.isValid?.()) {
        // intentionally no fitBounds here to avoid camera jumping on hover
      }
    } catch (error) {
      console.error('Failed while handling hover overlay:', error);
    }
  }, [feature]);

  if (!feature?.geometry) return null;

  const geometry = feature.geometry as GeoJSON.GeoJsonObject;

  return (
    <GeoJSON
      ref={geoJsonRef}
      key={JSON.stringify(geometry)}
      data={geometry}
      style={style}
      coordsToLatLng={coordsToLatLng}
    />
  );
};

interface CadastralMapSectionProps {
  isReview: boolean;
  readOnly?: boolean;
  allowAnalysisAreaAngleCopy?: boolean;
  isParcelInteractionDisabled?: boolean;
  mapType: GridMapType;
  mapViews: MapViewsState;
  setMapType: Dispatch<SetStateAction<GridMapType>>;
  setMapViews: Dispatch<SetStateAction<MapViewsState>>;
  mapCenter: [number, number];
  crs2056: L.CRS;
  gridParams: GridParams;
  Style: GridStyle;
  legalBorderRef: MutableRefObject<L.Polygon | null>;
  buildableBorderRef: MutableRefObject<L.Polygon | null>;
  analysisAreaRef: MutableRefObject<L.Polygon | null>;
  legalBorderInactiveRefs?: MutableRefObject<L.Polygon[]>;
  buildableBorderInactiveRefs?: MutableRefObject<L.Polygon[]>;
  analysisAreaInactiveRefs?: MutableRefObject<L.Polygon[]>;
  inactiveAnalysisAreaStyle?: L.PathOptions;
  activeAnalysisAreaIndex?: number;
  showInactiveNonAnalysisBorders?: boolean;
  showOnlyAnalysisAreas?: boolean;
  showInactiveAnalysisAreas?: boolean;
  isDrawingRef: MutableRefObject<boolean>;
  showEditableCoordinatesArea: boolean;
  AnalysisAreaCenterMarkerSync?: ComponentType<AnalysisAreaCenterMarkerSyncProps>;
  onCreated?: ComponentProps<typeof EditControl>['onCreated'];
  onDrawStart?: ComponentProps<typeof EditControl>['onDrawStart'];
  onDrawStop?: ComponentProps<typeof EditControl>['onDrawStop'];
  cadastralOpacity: number;
  focusedPolygonRef: MutableRefObject<L.Polygon | null> | null;
  coordinateSystem: CoordinateSystemPreference;
  cadastralLayerKey: number;
  onCadastralTileLoadStart?: L.LeafletEventHandlerFn;
  onCadastralTileLoad?: L.LeafletEventHandlerFn;
  onCadastralLayerLoad?: L.LeafletEventHandlerFn;
  onCadastralTileError?: L.LeafletEventHandlerFn;
  onCadastralParcelSelected?: (feature: GeoAdminIdentifyFeature) => void;
  onCadastralParcelMissClick?: () => void;
  onBeforeMapAction?: () => void;
  selectedParcelFeatures?: GeoAdminIdentifyFeature[];
  mapContent?: ReactNode;
  drawPathOptions?: L.PathOptions;
  showProjectLocationCircle?: boolean;
  zoomSnap?: number;
}

const CadastralMapSection = ({
  isReview,
  readOnly = false,
  allowAnalysisAreaAngleCopy = false,
  isParcelInteractionDisabled = false,
  mapType,
  mapViews,
  setMapType,
  setMapViews,
  mapCenter,
  crs2056,
  gridParams,
  Style,
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  legalBorderInactiveRefs,
  buildableBorderInactiveRefs,
  analysisAreaInactiveRefs,
  inactiveAnalysisAreaStyle,
  activeAnalysisAreaIndex = 0,
  showInactiveNonAnalysisBorders = false,
  showOnlyAnalysisAreas = false,
  showInactiveAnalysisAreas = true,
  isDrawingRef,
  showEditableCoordinatesArea,
  AnalysisAreaCenterMarkerSync,
  onCreated,
  onDrawStart,
  onDrawStop,
  cadastralOpacity,
  focusedPolygonRef,
  coordinateSystem,
  cadastralLayerKey,
  onCadastralTileLoadStart,
  onCadastralTileLoad,
  onCadastralLayerLoad,
  onCadastralTileError,
  onCadastralParcelSelected,
  onCadastralParcelMissClick,
  onBeforeMapAction,
  selectedParcelFeatures = [],
  mapContent,
  drawPathOptions,
  showProjectLocationCircle = false,
  zoomSnap = 0.25,
}: CadastralMapSectionProps) => {
  const context = useContext(ViewContext);
  const [hoveredParcelFeature, setHoveredParcelFeature] = useState<GeoAdminIdentifyFeature | null>(null);
  const analysisAreaClickHandlerRef = useRef<{
    layers: L.Polygon[];
    handler: (event: L.LeafletMouseEvent) => void | Promise<void>;
  } | null>(null);
  const isReadOnlyMap = readOnly || isReview;
  const copyTextToClipboard = useCallback(async (text: string) => {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (!successful) {
      throw new Error('copy_failed');
    }
  }, []);
  const handleRecenterMap = useCallback(() => {
    onBeforeMapAction?.();
    const analysisAreaCenter = getCombinedAnalysisAreasCenterLatLng(
      gridParams?.analysisArea,
      gridParams?.analysisAreas,
      null
    );
    const fallbackCenter =
      Array.isArray(mapCenter) && Number.isFinite(mapCenter[0]) && Number.isFinite(mapCenter[1]) ? mapCenter : null;
    const nextCenter = analysisAreaCenter ?? fallbackCenter;

    if (!nextCenter) {
      return;
    }

    setMapViews((prev) => ({
      ...prev,
      cadastral: {
        ...prev.cadastral,
        center: nextCenter,
      },
      osm: {
        ...prev.osm,
        center: nextCenter,
      },
    }));
  }, [gridParams?.analysisArea, gridParams?.analysisAreas, mapCenter, onBeforeMapAction, setMapViews]);
  const handleZoomChange = useCallback(
    (delta: number) => {
      onBeforeMapAction?.();
      const currentZoom = mapViews[mapType]?.zoom;
      if (!Number.isFinite(currentZoom)) {
        return;
      }

      const maxZoom = mapType === 'osm' ? 19 : 28;
      const minZoom = mapType === 'cadastral' ? CADASTRAL_MIN_ZOOM : 0;
      const nextZoom = Math.min(maxZoom, Math.max(minZoom, currentZoom + delta));

      if (nextZoom === currentZoom) {
        return;
      }

      setMapViews((prev) => synchronizeMapView(prev, mapType, { zoom: nextZoom }));
    },
    [mapType, mapViews, onBeforeMapAction, setMapViews]
  );
  const hoverOverlayStyle = useMemo(
    () => ({
      color: '#FFD54F',
      weight: 3,
      opacity: 1,
      fillColor: '#FFD54F',
      fillOpacity: 0.18,
    }),
    []
  );
  const selectedOverlayStyle = useMemo(
    () => ({
      color: '#7c3aed',
      weight: 3,
      opacity: 1,
      fillColor: '#a855f7',
      fillOpacity: 0.22,
    }),
    []
  );

  useEffect(() => {
    if (!isParcelInteractionDisabled) {
      return;
    }
    setHoveredParcelFeature(null);
  }, [isParcelInteractionDisabled]);

  useEffect(() => {
    if ((!allowAnalysisAreaAngleCopy && isReadOnlyMap) || !gridParams.analysisArea.length) {
      if (analysisAreaClickHandlerRef.current?.layers && analysisAreaClickHandlerRef.current?.handler) {
        analysisAreaClickHandlerRef.current.layers.forEach((layer) =>
          layer.off('click', analysisAreaClickHandlerRef.current?.handler)
        );
        analysisAreaClickHandlerRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attachClickHandler = () => {
      if (cancelled) {
        return;
      }

      const layers = [analysisAreaRef.current, ...(analysisAreaInactiveRefs?.current ?? [])].filter(
        (layer): layer is L.Polygon => Boolean(layer)
      );
      if (!layers.length || !layers.every((layer) => getLayerMap(layer))) {
        retryTimer = setTimeout(attachClickHandler, 50);
        return;
      }

      if (analysisAreaClickHandlerRef.current?.layers && analysisAreaClickHandlerRef.current?.handler) {
        analysisAreaClickHandlerRef.current.layers.forEach((layer) =>
          layer.off('click', analysisAreaClickHandlerRef.current?.handler)
        );
      }

      const handleAnalysisAreaClick = async (event: L.LeafletMouseEvent) => {
        const isEditing =
          isDrawingRef.current ||
          analysisAreaRef.current?.pm?.enabled?.() ||
          legalBorderRef.current?.pm?.enabled?.() ||
          buildableBorderRef.current?.pm?.enabled?.();

        if (isEditing) {
          return;
        }

        const clickedLayer = event.target instanceof L.Polygon ? event.target : null;
        if (!clickedLayer) {
          return;
        }

        const latlngs = getLayerOuterRing(clickedLayer);
        const angle = getSelectedSideAngle(
          event.latlng,
          latlngs,
          getLayerMap(clickedLayer),
          CADASTRAL_EDGE_CLICK_MAX_DISTANCE_PX
        );
        if (angle == null) {
          return;
        }

        const roundedAngle = (Math.round(angle * 100) / 100) % 360;
        try {
          await copyTextToClipboard(String(roundedAngle));
          context.notification.show(
            roundedAngle + '° - The angle of the selected side has been successfully copied to clipboard!',
            'success',
            true,
            'toast'
          );
        } catch {
          context.notification.show('Unable to copy the angle to clipboard.', 'error', true, 'toast');
        }
      };

      analysisAreaClickHandlerRef.current = { layers, handler: handleAnalysisAreaClick };
      layers.forEach((layer) => layer.on('click', handleAnalysisAreaClick));
    };

    attachClickHandler();

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      if (analysisAreaClickHandlerRef.current?.layers && analysisAreaClickHandlerRef.current?.handler) {
        analysisAreaClickHandlerRef.current.layers.forEach((layer) =>
          layer.off('click', analysisAreaClickHandlerRef.current?.handler)
        );
        analysisAreaClickHandlerRef.current = null;
      }
    };
  }, [
    analysisAreaRef,
    analysisAreaInactiveRefs,
    buildableBorderRef,
    context.notification,
    copyTextToClipboard,
    gridParams.analysisArea.length,
    isDrawingRef,
    isReadOnlyMap,
    allowAnalysisAreaAngleCopy,
    legalBorderRef,
  ]);

  return (
    <div className="grid-map-shell">
      {mapViews[mapType]?.center?.[0] !== 0 && mapViews[mapType]?.center?.[1] !== 0 && (
        <>
          <div className="grid-map-shell__switcher">
            <ShowHideButtonsForMaps
              mapType={mapType}
              setMapType={setMapType}
              onBeforeChangeMapType={onBeforeMapAction}
            />
          </div>
          <div style={mapControlsStyle} className="grid-map-controls leaflet-control">
            <button
              type="button"
              onClick={() => handleZoomChange(1)}
              style={mapControlButtonStyle}
              aria-label="Zoom in"
            >
              <svg width={12} height={12} viewBox="0 0 16 16" style={{ cursor: 'pointer' }}>
                <rect x={7} y={2} width={2} height={12} rx={1} fill="currentColor" />
                <rect x={2} y={7} width={12} height={2} rx={1} fill="currentColor" />
              </svg>
            </button>
            <div style={mapControlDividerStyle} />
            <button
              type="button"
              onClick={() => handleZoomChange(-1)}
              style={mapControlButtonStyle}
              aria-label="Zoom out"
            >
              <svg width={12} height={12} viewBox="0 0 16 16" style={{ cursor: 'pointer' }}>
                <rect x={2} y={7} width={12} height={2} rx={1} fill="currentColor" />
              </svg>
            </button>
            <div style={mapControlDividerStyle} />
            <button type="button" onClick={handleRecenterMap} style={mapControlButtonStyle} aria-label="Recenter map">
              <Target03Icon style={{ width: 18, height: 18, cursor: 'pointer', color: '#64748b' }} />
            </button>
          </div>
          <MapContainer
            key={mapType}
            crs={mapType === 'osm' ? L.CRS.EPSG3857 : crs2056}
            center={mapViews[mapType].center}
            zoom={mapViews[mapType].zoom}
            scrollWheelZoom={true}
            zoomControl={false}
            doubleClickZoom={true}
            zoomSnap={zoomSnap}
            minZoom={mapType === 'cadastral' ? CADASTRAL_MIN_ZOOM : 0}
            maxZoom={mapType === 'osm' ? 19 : 28}
            className="map-container-component"
            style={{ height: 'var(--project-creation-map-height)', width: '100%' }}
          >
            <MapTracker
              mapType={mapType}
              setMapViews={setMapViews}
              isDrawingRef={isDrawingRef}
              viewCenter={mapViews[mapType].center}
              viewZoom={mapViews[mapType].zoom}
              resizeSignal={showEditableCoordinatesArea}
              focusLayerRef={focusedPolygonRef}
            />

            {AnalysisAreaCenterMarkerSync ? (
              <AnalysisAreaCenterMarkerSync
                mapType={mapType}
                analysisArea={gridParams?.analysisArea}
                analysisAreas={gridParams?.analysisAreas}
                manualCenter={gridParams?.manualCenter}
              />
            ) : null}

            <MousePositionPopup coordinateSystem={coordinateSystem} />
            <Copyright mapType={mapType} />

            <RenderIfThereIsAPolygon
              gridParams={gridParams}
              Style={Style}
              legalBorderRef={legalBorderRef}
              buildableBorderRef={buildableBorderRef}
              analysisAreaRef={analysisAreaRef}
              legalBorderInactiveRefs={legalBorderInactiveRefs}
              buildableBorderInactiveRefs={buildableBorderInactiveRefs}
              analysisAreaInactiveRefs={analysisAreaInactiveRefs}
              showInactiveNonAnalysisBorders={showInactiveNonAnalysisBorders}
              showOnlyAnalysisAreas={showOnlyAnalysisAreas}
              showInactiveAnalysisAreas={showInactiveAnalysisAreas}
              inactiveAnalysisAreaStyle={inactiveAnalysisAreaStyle}
              activeAnalysisAreaIndex={activeAnalysisAreaIndex}
            />

            {mapContent}

            {!isReadOnlyMap ? (
              <FeatureGroup>
                <EditControl
                  position="topright"
                  onCreated={onCreated}
                  onDrawStart={onDrawStart}
                  onDrawStop={onDrawStop}
                  draw={{
                    polygon: {
                      showArea: true,
                      ...(drawPathOptions ? { shapeOptions: drawPathOptions } : {}),
                    },
                    rectangle: {
                      showArea: true,
                      ...(drawPathOptions ? { shapeOptions: drawPathOptions } : {}),
                    },
                    circle: false,
                    polyline: false,
                    marker: false,
                    circlemarker: false,
                  }}
                  edit={{
                    edit: false,
                    remove: false,
                  }}
                />
              </FeatureGroup>
            ) : null}

            {mapType === 'osm' && (
              <Fragment>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  zIndex={1}
                  maxZoom={19}
                />
              </Fragment>
            )}

            {mapType === 'cadastral' && (
              <Fragment>
                {renderCadastralLayer({
                  cadastralOpacity,
                  crs2056,
                  cadastralLayerKey,
                  onCadastralTileLoadStart,
                  onCadastralTileLoad,
                  onCadastralLayerLoad,
                  onCadastralTileError,
                })}

                <CadastralParcelInteraction
                  enabled={!isReadOnlyMap && !isParcelInteractionDisabled}
                  isDrawingRef={isDrawingRef}
                  onHoverFeatureChange={setHoveredParcelFeature}
                  onParcelSelected={onCadastralParcelSelected}
                  onParcelMissClick={onCadastralParcelMissClick}
                />

                <CadastralFeatureOverlay feature={hoveredParcelFeature} style={hoverOverlayStyle} />
                {selectedParcelFeatures.map((feature, index) => (
                  <CadastralFeatureOverlay
                    key={`${feature.featureId ?? feature.id ?? feature.label ?? 'parcel'}-${index}`}
                    feature={feature}
                    style={selectedOverlayStyle}
                  />
                ))}
              </Fragment>
            )}

            {showProjectLocationCircle && (
              <Circle
                center={mapCenter}
                radius={PROJECT_LOCATION_RADIUS_METERS}
                pathOptions={{
                  className: 'project-location-radius-outline',
                  color: '#2f80ed',
                  opacity: 0.55,
                  weight: 3,
                  fill: false,
                }}
              />
            )}
            <Marker position={mapCenter}></Marker>
          </MapContainer>
        </>
      )}
    </div>
  );
};

export default CadastralMapSection;
